import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    // iOS only grants inline autoplay when these are set as properties. The HTML
    // attributes on their own are not enough once load() restarts the pipeline.
    videoElement.muted = true;
    videoElement.playsInline = true;

    videoElement.load();

    const { signal } = this.#abortController;

    // load() discards the browser's initial autoplay attempt, so start playback
    // again once there are frames, and retry when the page is shown or restored.
    videoElement.addEventListener('canplay', this.#play, { signal });
    document.addEventListener('visibilitychange', this.#play, { signal });
    window.addEventListener('pageshow', this.#play, { signal });

    this.#play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Starts playback, ignoring rejections from browsers that refuse autoplay so
   * the preview image stays visible instead (iOS Low Power Mode, for example).
   */
  #play = () => {
    const { videoElement } = this.refs;

    if (!videoElement || document.hidden || !videoElement.paused) return;

    videoElement.play().catch(() => {});
  };
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
